import PropTypes from "prop-types";
import React, { memo, useEffect, useRef, useState } from "react";

import { css } from "styled-components";
import styled from "styled-components/macro";

import { useCellMeasurements } from "Boards";
import { getRange } from "CrossFeatureComponents/ExtendedFields/NumberField";

import { FnrCombobox } from "UI/FnrSelectList3";
import { colors } from "UI/colors";

import { useDebounce } from "Utilities/Hooks/useDebounce";

import { cellContainerStyles } from "./cellContainerStyles";
import {
  getIsInline,
  getIsValid,
  getIsYear,
  getTooltipText,
} from "./numberCellUtils";

const NumberCell = memo(WrappedComponent);
export default NumberCell;

WrappedComponent.propTypes = {
  columnID: PropTypes.number,
  entityID: PropTypes.number,
  value: PropTypes.string,
  saveCellValue: PropTypes.func,
  canEdit: PropTypes.bool,
  extendedFieldData: PropTypes.object,
};

function WrappedComponent({
  columnID,
  entityID,
  entityType,
  value = "",
  saveCellValue,
  canEdit,
  extendedFieldData,
}) {
  const inputRef = useRef();
  const [inputVal, setInputVal] = useState(value);

  const [mostRecentValue, setMostRecentValue] = useState(value);

  const [shouldShowError, setShouldShowError] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  const inputValAsNum = Number.parseFloat(inputVal);

  const isYear = getIsYear({ extendedFieldData });
  const isInline = getIsInline({ extendedFieldData });
  const debouncedInputVal = useDebounce(inputVal, 500);
  const isValid = getIsValid({ value: debouncedInputVal, extendedFieldData });

  const tooltipText = getTooltipText({
    value: debouncedInputVal,
    extendedFieldData,
  });

  // let isInline = true;

  // if (isExtendedField) {
  //   const { minimum, maximum } = extendedFieldData;
  //   const hasMinimum = typeof minimum === "number";
  //   const hasMaximum = typeof maximum === "number";

  //   if (hasMinimum && hasMaximum) {
  //     const rangeLength = maximum - minimum;
  //     isInline =
  //       Number.isNaN(rangeLength) ||
  //       rangeLength > MAXIMUM_SELECT_LIST_RANGE_LENGTH;
  //   }

  //   if (isInline) {
  //     if (hasMinimum && inputValAsNum < minimum) {
  //       tooltipText = `Must be greater than or equal to ${minimum}`;
  //     } else if (hasMaximum && inputValAsNum > maximum) {
  //       tooltipText = `Must be less than or equal to ${maximum}`;
  //     }
  //   }
  // }
  // const isValid = tooltipText === inputVal;

  // Used to update cell value when a Shared Field cell value is updated
  useEffect(
    function overrideInputValWithPropVal() {
      const newValuePropReceived =
        value !== mostRecentValue && value !== inputVal;

      // only update value if needed and cell is not in focus.
      if (newValuePropReceived && !hasFocus) {
        setInputVal(value);
        setMostRecentValue(value);
      }
    },
    [value, inputVal, mostRecentValue, hasFocus]
  );

  const save = (val) => {
    saveCellValue({
      columnID,
      entityID,
      entityType,
      value: transformValueToSaveShape(val),
      origValue: transformValueToSaveShape(value),
    });
  };

  const handleInlineFocus = () => setHasFocus(true);

  const handleInlineChange = (e) => {
    const newVal = e.target.value;

    // character limit
    if ((isYear && newVal.length > 4) || newVal.length > 100) return;

    // only digits for a year, otherwise one decimal point is allowed
    if (
      !(
        newVal === "" ||
        (isYear && RegExp(/^[0-9]*$/).test(newVal)) ||
        (!isYear && RegExp(/^[0-9]*\.?[0-9]*$/).test(newVal))
      )
    ) {
      return;
    }

    setShouldShowError(false);
    setInputVal(newVal);
    remeasureCell(newVal);
  };

  const handleSelectListChange = (val) => {
    save(val);
    setInputVal(val);
    remeasureCell(val);
  };

  useEffect(() => {
    if (!hasFocus) return; // only save if updated directly (not via shared cell change)
    if (String(debouncedInputVal) === String(value)) return; // don't save if value hasn't changed
    if (!isValid) return; // don't save if value is not valid

    saveCellValue({
      columnID,
      entityID,
      value: transformValueToSaveShape(debouncedInputVal),
      origValue: transformValueToSaveShape(value),
    });
  }, [
    debouncedInputVal,
    hasFocus,
    isValid,
    value,
    saveCellValue,
    columnID,
    entityID,
  ]);

  const blurTheInput = (e) => {
    e.preventDefault();
    if (!(inputRef?.current instanceof HTMLInputElement)) return;

    inputRef.current.blur();
  };

  const handleInlineBlur = () => {
    if (!isValid) setShouldShowError(true);

    // need to use a setTimeout in order to not block debouced save call
    setTimeout(() => {
      setHasFocus(false);
    }, 1000);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") blurTheInput(e);
  };

  const { MeasureCell, remeasureCell } = useCellMeasurements({ columnID });

  const isDisabled = !canEdit;

  return (
    <>
      <Wrapper title={tooltipText}>
        {isInline || isDisabled ? (
          <form onSubmit={blurTheInput}>
            <Input
              data-cy="numCell"
              ref={inputRef}
              value={inputVal}
              disabled={isDisabled}
              onFocus={handleInlineFocus}
              onChange={handleInlineChange}
              onBlur={handleInlineBlur}
              onKeyDown={handleInputKeyDown}
              placeholder="0"
              className={shouldShowError ? "is-invalid" : ""}
            />
          </form>
        ) : (
          <SelectList
            selection={inputValAsNum}
            onSelect={handleSelectListChange}
            options={getRange(
              extendedFieldData.minimum,
              extendedFieldData.maximum
            )}
            openIndicator={false}
            className={isDisabled ? "is-disabled" : ""}
          />
        )}
      </Wrapper>
      {inputVal.length > 0 && (
        <MeasureCell>
          <MeasureStyling>{inputVal}</MeasureStyling>
        </MeasureCell>
      )}
    </>
  );
}

function transformValueToSaveShape(val) {
  return {
    value: typeof val === "number" ? val : Number.parseFloat(val) || "",
  };
}

const commonStyles = css`
  font-family: "Lucida Console", Monaco, monospace;
  text-align: right;
  border: 1px solid rgba(0, 0, 0, 0.05);

  &:focus {
    border: 1px solid #278dc1;
    background: rgba(39, 141, 193, 0.05);
  }
`;

const Wrapper = styled.div`
  box-sizing: border-box;
  cursor: pointer;
  position: relative;
`;

const Input = styled.input`
  ${cellContainerStyles};
  ${commonStyles};

  &:disabled {
    border: none;
  }

  &::placeholder {
    color: transparent;
  }

  &:focus::placeholder {
    color: #ccc;
  }

  &.is-invalid {
    border-color: ${colors.fnRed};
  }
`;

const SelectList = styled(FnrCombobox)`
  ${cellContainerStyles};
  ${commonStyles};

  &.is-disabled {
    border: none;
  }
`;

const MeasureStyling = styled.div`
  white-space: nowrap;
  font-weight: 400;
  box-sizing: border-box;
  font-family: "Lucida Console", Monaco, monospace;
  padding-right: 20px;
  font-size: 13px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;
